/**
 * How analytics numbers read (docs/analytics.md §1): counts, watch time in hours and minutes,
 * percentages, days, and the change against the previous period. A change on a small base (under
 * 20) is said as a difference ("+3"), never a percentage ("+300%" from 1 to 4 misleads a small
 * brand); with nothing to compare (no previous figure, or nothing at all in the previous period,
 * as for a brand in its first weeks) it is a dash.
 *
 * Pure, English, UTC (the console's language; the ranges are UTC days). No imports at run time.
 */

/** The base under which a change is absolute rather than a percentage. */
export const SMALL_BASE = 20;

/** A dash: no data, or nothing to compare with. */
export const NONE = "—";

const whole = new Intl.NumberFormat("en", { maximumFractionDigits: 0 });
const compact = new Intl.NumberFormat("en", { notation: "compact", maximumFractionDigits: 1 });

/** 1,284 · 12.9K · 1.2M (exact under 10,000). */
export function count(n: number | null | undefined): string {
  if (n === null || n === undefined || !Number.isFinite(n)) return NONE;
  return Math.abs(n) < 10_000 ? whole.format(n) : compact.format(n);
}

/** Watch time: "45 s", "12 min", "3 h 20 min", "1,234 h". */
export function duration(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined || !Number.isFinite(seconds)) return NONE;
  const s = Math.max(0, Math.round(seconds));
  if (s < 60) return `${s} s`;
  if (s < 3600) return `${Math.round(s / 60)} min`;
  const hours = Math.floor(s / 3600);
  if (hours >= 100) return `${whole.format(Math.round(s / 3600))} h`;
  const minutes = Math.round((s % 3600) / 60);
  return minutes === 60 ? `${hours + 1} h` : minutes ? `${hours} h ${minutes} min` : `${hours} h`;
}

/** A position in a video: "2:05", "1:02:05". */
export function clock(seconds: number): string {
  const s = Math.max(0, Math.round(seconds));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = String(s % 60).padStart(2, "0");
  return h ? `${h}:${String(m).padStart(2, "0")}:${sec}` : `${m}:${sec}`;
}

/** part ÷ whole, or null when the whole is 0 (a rate of nothing is no rate). */
export function rate(part: number, whole: number): number | null {
  return whole > 0 ? part / whole : null;
}

/** A share (0–1) as a percentage: "42%", "4.5%" under 10, "—" when there is none. */
export function percent(share: number | null | undefined): string {
  if (share === null || share === undefined || !Number.isFinite(share)) return NONE;
  const p = share * 100;
  return p > 0 && p < 10 ? `${p.toFixed(1)}%` : `${Math.round(p)}%`;
}

export type Direction = "up" | "down" | "flat" | "none";

export interface Change {
  /** What the tile shows: "+12%", "−3", "No change", "—". */
  text: string;
  direction: Direction;
  /** The sentence a screen reader hears. */
  spoken: string;
}

/**
 * The change against the previous period. `format` writes an absolute difference (a duration for
 * watch time); percentages are whole numbers.
 */
export function change(current: number | null | undefined, previous: number | null | undefined, format: (n: number) => string = count): Change {
  if (current === null || current === undefined || previous === null || previous === undefined) {
    return { text: NONE, direction: "none", spoken: "Nothing to compare with yet" };
  }
  if (previous === 0) return { text: NONE, direction: "none", spoken: "Nothing in the previous period to compare with" };
  const diff = current - previous;
  if (diff === 0) return { text: "No change", direction: "flat", spoken: "No change on the previous period" };
  const direction: Direction = diff > 0 ? "up" : "down";
  const sign = diff > 0 ? "+" : "−";
  const text = previous < SMALL_BASE ? `${sign}${format(Math.abs(diff))}` : `${sign}${whole.format(Math.abs(Math.round((diff / previous) * 100)))}%`;
  return { text, direction, spoken: `${diff > 0 ? "Up" : "Down"} ${text.slice(1)} on the previous period` };
}

/** The change in a share (completion, engaged), always in percentage points: "+3 pts". */
export function shareChange(current: number | null | undefined, previous: number | null | undefined): Change {
  if (current === null || current === undefined || previous === null || previous === undefined) {
    return { text: NONE, direction: "none", spoken: "Nothing to compare with yet" };
  }
  const points = Math.round((current - previous) * 100);
  if (points === 0) return { text: "No change", direction: "flat", spoken: "No change on the previous period" };
  const text = `${points > 0 ? "+" : "−"}${Math.abs(points)} pts`;
  return { text, direction: points > 0 ? "up" : "down", spoken: `${points > 0 ? "Up" : "Down"} ${Math.abs(points)} percentage points on the previous period` };
}

const DAY_FORMAT = new Intl.DateTimeFormat("en-GB", { day: "numeric", month: "short", timeZone: "UTC" });
const DAY_YEAR_FORMAT = new Intl.DateTimeFormat("en-GB", { day: "numeric", month: "short", year: "numeric", timeZone: "UTC" });

/** "3 Oct" for a YYYY-MM-DD day (UTC). */
export function dayLabel(day: string): string {
  return DAY_FORMAT.format(new Date(`${day}T00:00:00Z`));
}

/** "1 Oct – 28 Oct 2026". */
export function rangeText(range: { from: string; to: string }): string {
  return `${dayLabel(range.from)} – ${DAY_YEAR_FORMAT.format(new Date(`${range.to}T00:00:00Z`))}`;
}

/** "14:05 UTC" for a minute (ms or ISO), the clock the ranges use. */
export function minuteLabel(at: number | string): string {
  const d = new Date(at);
  return Number.isNaN(d.getTime()) ? NONE : `${String(d.getUTCHours()).padStart(2, "0")}:${String(d.getUTCMinutes()).padStart(2, "0")} UTC`;
}

/**
 * Sales times their display prices ("£4.99"), when every price is known and in the same currency
 * (the same symbols around the figure); otherwise null, and the screen says so rather than guess.
 */
export function estimatedTotal(sales: ReadonlyArray<{ count: number; price: string | null }>): string | null {
  const sold = sales.filter((s) => s.count > 0);
  if (!sold.length) return null;
  let shape: string | null = null;
  let total = 0;
  let decimals = 0;
  for (const s of sold) {
    const m = s.price?.trim().match(/^([^\d.,-]*)\s*(\d{1,3}(?:,\d{3})*|\d+)(?:\.(\d+))?\s*([^\d.,]*)$/);
    if (!m) return null;
    const key = `${m[1]!.trim()}|${m[4]!.trim()}`;
    if (shape !== null && shape !== key) return null;
    shape = key;
    decimals = Math.max(decimals, m[3]?.length ?? 0);
    total += Number(`${m[2]!.replace(/,/g, "")}.${m[3] ?? "0"}`) * s.count;
  }
  const [before, after] = shape!.split("|");
  const figure = new Intl.NumberFormat("en", { minimumFractionDigits: decimals, maximumFractionDigits: decimals }).format(total);
  return `${before}${figure}${after ? ` ${after}` : ""}`;
}

/** "3 Oct, 14:05 UTC" for a moment (ms or ISO). */
export function momentLabel(at: number | string): string {
  const d = new Date(at);
  return Number.isNaN(d.getTime()) ? NONE : `${DAY_FORMAT.format(d)}, ${minuteLabel(at)}`;
}
